import { useSearchParams } from 'react-router';
import { useApiClients } from '../../client/api/useApiClient';
import { useQuery } from '@tanstack/react-query';
import {
  Department,
  GetAllDepartmentResponse,
  GetEstablishmentResponseDto,
  GetSubmmissionDetailsResponse,
  OshaReportingControllerGetOshaSubmissionDetailsClassificationEnum,
  OshaReportingControllerGetOshaSubmissionDetailsStatusEnum,
  OshaReportingControllerGetOshaSubmissionDetailsTypeEnum,
  OshaReportingControllerGetOshaSubmissionDetailsTypeOfLossEnum,
} from '@EHSNavigator/backend';
import Loader from '../../components/Loader';
import OshaReportingLogs from '../../views/OshaReportingLogs';

export default function OshaReportingLogsPage() {
  const { DepartmentApi, OshaReportingApi, EstablishmentApi } = useApiClients();
  const [searchParams] = useSearchParams();
  const queryYear = searchParams.get('year') ?? '2024';
  const searchQuery = searchParams.get('search') ?? '';
  const department = searchParams.get('department') ?? '';
  const status = searchParams.get('status') ?? '';
  const classification = searchParams.get('classification') ?? '';
  const typeOfLoss = searchParams.get('typeOfLoss') ?? '';
  const type = searchParams.get('type') ?? '';
  const currentPage = searchParams.get('page') ?? 1;
  const limit = searchParams.get('limit') ?? 10;

  const query: any = {
    year: Number(queryYear) || 2024,
    page: Number(currentPage) || 1,
  };

  if (searchQuery) query.search = searchQuery;
  if (department) query.departmentId = department;
  if (status) query.status = status;
  if (classification) query.classification = classification;
  if (typeOfLoss) query.typeOfLoss = typeOfLoss;
  if (type) query.type = type;
  if (limit) query.limit = Number(limit);

  const { data, isLoading, isRefetching } = useQuery<
    GetSubmmissionDetailsResponse,
    Error,
    any
  >({
    queryKey: [
      'get-osha-reporting-logs',
      Number(queryYear),
      searchQuery,
      currentPage,
      Number(limit),
      department,
      status,
      classification,
      typeOfLoss,
      type,
    ],
    queryFn: async () => {
      const response =
        await OshaReportingApi.oshaReportingControllerGetOshaSubmissionDetails({
          ...query,
        });
      return response?.data;
    },
  });
  const {
    data: establishment,
    isLoading: isEstablishmentLoading,
    isRefetching: isEstablishmentRefetching,
  } = useQuery<GetEstablishmentResponseDto['establishment'], Error, any>({
    queryKey: ['get-establishment'],
    queryFn: async () => {
      const response =
        await EstablishmentApi.establishmentControllerGetEstablishment();
      return response?.data?.establishment;
    },
  });

  const { data: allDepartments, isLoading: isDepartmentsLoading } =
    useQuery<GetAllDepartmentResponse>({
      queryKey: ['my-reported-departments'],
      queryFn: async () => {
        const response =
          await DepartmentApi.departmentControllerFindAllDepartment({
            disablePagination: true,
          });
        return response?.data;
      },
    });

  const departments =
    allDepartments?.departments?.map((node: Department) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const hasFilters =
    !!queryYear ||
    !!searchQuery ||
    !!department ||
    !!status ||
    !!classification ||
    !!typeOfLoss ||
    !!type;

  const noDataMessage = hasFilters
    ? 'No items match your filters or search. Try using different keywords maybe.'
    : "You haven't reported an incident yet";

  const isLogsNotAvailable = [21, 1].includes(establishment?.size);

  if (
    isDepartmentsLoading ||
    isEstablishmentLoading ||
    isEstablishmentRefetching
  ) {
    return <Loader />;
  }

  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <OshaReportingLogs
        params={{
          year: Number(queryYear),
          search: searchQuery,
          page: Number(currentPage),
          limit: Number(limit),
          departmentId: department,
          status:
            status as OshaReportingControllerGetOshaSubmissionDetailsStatusEnum,
          classification:
            classification as OshaReportingControllerGetOshaSubmissionDetailsClassificationEnum,
          typeOfLoss:
            typeOfLoss as OshaReportingControllerGetOshaSubmissionDetailsTypeOfLossEnum,
          type: type as OshaReportingControllerGetOshaSubmissionDetailsTypeEnum,
        }}
        isIncidentsLoading={isLoading || isRefetching}
        departments={departments}
        data={data}
        isLogsNotAvailable={isLogsNotAvailable}
        noDataMessage={noDataMessage}
      />
    </div>
  );
}
